import { Autocomplete, Box, Button, Collapse, FormControlLabel, FormGroup, IconButton, InputAdornment, InputLabel, Skeleton, Stack, Switch, TextField, Tooltip, Typography } from "@mui/material";
import { Dispatch, MutableRefObject, SetStateAction, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowForward, ArrowUpwardRounded, AutoFixHighRounded, ChevronRightRounded } from "@mui/icons-material";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import { enqueueAction } from "../../../engine/queue";
import { useGetLangsMappingQuery } from "../../services/instance";
import { AnimatedText } from "./AnimatedText";
import { GeneratedImage } from "./GeneratedImage";
import { PromptComposer, PromptComposerHandle } from "./PromptComposer";
import { useUpdatePromptApprovalStatusMutation } from "../../services/mcp";
import { useApplySetValue } from "./useApplySetValue";

// `value` is the tone instruction sent to the model (do not translate);
// `label` is the short UI name, resolved with t() at the render site.
export const TONE_VALUES = {
  intriguing: "Intriguing - Curious, mysterious, and thought-provoking",
  professional: "Professional - Serious, formal, and authoritative",
  playful: "Playful - Fun, light-hearted, and whimsical",
  sensational: "Sensational -  Bold, dramatic, and attention-grabbing",
  succinct: "Succinct - Clear, factual, with no hyperbole"
} as const;
const getToneOptions = (t: (key: string) => string) => [{
  label: t("shell.toneNameIntriguing"),
  value: TONE_VALUES.intriguing
}, {
  label: t("shell.toneNameProfessional"),
  value: TONE_VALUES.professional
}, {
  label: t("shell.toneNamePlayful"),
  value: TONE_VALUES.playful
}, {
  label: t("shell.toneNameSensational"),
  value: TONE_VALUES.sensational
}, {
  label: t("shell.toneNameSuccinct"),
  value: TONE_VALUES.succinct
}] as const;
type LanguageOption = {
  label: string;
  value: string;
};
type ToneOption = {
  label: string;
  value: string;
};
export type ChatResponse = {
  approval?: string;
} & ({
  type: "USER_INPUT";
  payload: {
    value: string;
  };
} | {
  type: "NAVIGATE";
  payload: {
    path: string;
  };
} | {
  type: "SET_VALUE";
  payload: {
    refKey: string;
    value: string;
  };
}
// A prose reply.
| {
  type: "MESSAGE";
  payload: {
    value: string;
  };
}
// Built by a host, not sent by the server: an applied code edit's line
// counts, and a line of its own.
| {
  type: "CODE_EDIT";
  payload: {
    fileName: string;
    added: number;
    removed: number;
  };
} | {
  type: "NOTICE";
  payload: {
    value: string;
  };
} | {
  type: "SYSTEM_SUGGESTION" | "SYSTEM_OUTPUT" | "ERROR";
  payload: {
    refKey?: string;
    value: string;
  };
});

// Scrolls only the transcript. `scrollIntoView` would also scroll every
// ancestor that overflows, moving the host's own layout.
export const scrollToEnd = (container: HTMLElement | null) => container?.scrollTo({
  top: container.scrollHeight,
  behavior: "smooth"
});
export type ChatThreadProps = {
  responses: Record<string, ChatResponse[]>;
  setResponses: Dispatch<SetStateAction<Record<string, ChatResponse[]>>>;
  latestPromptZUIDs: Set<string>;
  autoApply: boolean;
  setAutoApply: (autoApply: boolean) => void;
  isInCodeApp: boolean;
  isLoading: boolean;
  isLoadingChatSessionLog: boolean;
  urlChatZUID: string | undefined;
  onClearChat: () => void;
  updatePromptApprovalStatus: ReturnType<typeof useUpdatePromptApprovalStatusMutation>[0];
  composerSeed: string;
  setComposerSeed: (seed: string) => void;
  selectedLanguage: LanguageOption;
  setSelectedLanguage: (language: LanguageOption) => void;
  selectedTone: ToneOption;
  setSelectedTone: (tone: ToneOption) => void;
  handlePrompt: (value: string) => boolean;
  handleGenerateSuggestions: (sourcePrompt: string) => void;
  // The transcript's own scroll box; scroll it with `scrollToEnd`.
  scrollContainerRef: MutableRefObject<HTMLDivElement | null>;
  // For a host that applies every suggestion itself.
  hideAutoApply?: boolean;
  placeholder?: string;
};
export const ChatThread = ({
  responses,
  setResponses,
  latestPromptZUIDs,
  autoApply,
  setAutoApply,
  isInCodeApp,
  isLoading,
  isLoadingChatSessionLog,
  urlChatZUID,
  onClearChat,
  updatePromptApprovalStatus,
  composerSeed,
  setComposerSeed,
  selectedLanguage,
  setSelectedLanguage,
  selectedTone,
  setSelectedTone,
  handlePrompt,
  handleGenerateSuggestions,
  scrollContainerRef,
  hideAutoApply = false,
  placeholder
}: ChatThreadProps) => {
  const {
    t
  } = useTranslation();
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [hasComposerValue, setHasComposerValue] = useState(false);
  const composerRef = useRef<PromptComposerHandle>(null);
  const {
    data: langMappings
  } = useGetLangsMappingQuery();
  const applySetValue = useApplySetValue();
  const toneOptions = getToneOptions(t);
  const generateSuggestionsPromptPrefix = t("shell.generateSuggestionsPrompt", {
    prompt: ""
  });
  const generateSuggestionsDefaultPrompt = t("shell.generateSuggestionsDefaultPrompt");
  const languageOptions = Object.entries(langMappings || {})?.map(([value, label]: any) => ({
    label,
    value
  }));
  return <>
      <Box display="flex" flexGrow={1} overflow="auto" data-sentry-element="Box" data-sentry-source-file="ChatThread.tsx">
        <Box ref={scrollContainerRef} display="block" overflow="auto" width="100%" pl={2} sx={{
        scrollbarGutter: "stable"
      }} data-sentry-element="Box" data-sentry-source-file="ChatThread.tsx">
          <Box flex="1" display="flex" flexDirection="column" gap={2} ref={chatContainerRef} justifyContent="flex-end"
        // mt={1}
        sx={{
          position: "relative",
          boxSizing: "border-box",
          minHeight: "100%",
          width: "100%",
          pb: 2
        }} data-sentry-element="Box" data-sentry-source-file="ChatThread.tsx">
            {isLoadingChatSessionLog ? <>
                <Skeleton data-cy="AIDrawerChatSessionLogSkeleton" variant="rounded" width="60%" height={40} sx={{
              ml: "auto",
              borderRadius: 2
            }} />
                <Skeleton variant="rounded" width="75%" height={64} sx={{
              borderRadius: 2
            }} />
              </> : Object.entries(responses).map(([promptZUID, promptResponses]) => {
            return promptResponses.map((response, responseIndex) => {
              const shouldAnimate = latestPromptZUIDs.has(promptZUID) && response.type !== "USER_INPUT";
              if (response.type === "USER_INPUT") {
                return <Box data-cy="AIDrawerUserInput" key={`${promptZUID}-${responseIndex}`} px={1.5} py={1} mt={responseIndex === 0 ? 1 : 0} sx={{
                  borderRadius: 2,
                  maxWidth: "184px",
                  width: "fit-content",
                  color: "white",
                  ml: "auto",
                  backgroundColor: "grey.500"
                }}>
                        <Typography variant="body2" sx={{
                    borderRadius: 1,
                    wordBreak: "break-word",
                    fontStyle: (response.payload.value.startsWith(generateSuggestionsPromptPrefix) || response.payload.value === generateSuggestionsDefaultPrompt) && "italic"
                  }}>
                          {response.payload.value}
                        </Typography>
                      </Box>;
              } else if (response.type === "SYSTEM_SUGGESTION") {
                return <Button data-cy="AIDrawerSystemSuggestion" key={`${promptZUID}-${responseIndex}`} onClick={() => {
                  setComposerSeed(response.payload.value);
                }} sx={{
                  textAlign: "left",
                  justifyContent: "flex-start",
                  width: "fit-content",
                  px: 1.5,
                  py: 1,
                  borderRadius: 2
                }} variant="contained" color="inherit" endIcon={<ChevronRightRounded />}>
                        <AnimatedText text={response.payload.value} animate={shouldAnimate} onGrow={() => scrollToEnd(scrollContainerRef.current)} />
                      </Button>;
              } else if (response.type === "NAVIGATE") {
                return <Button data-cy="AIDrawerNavigate" key={`${promptZUID}-${responseIndex}`} variant="contained" sx={{
                  width: "fit-content"
                }} onClick={() => {
                  enqueueAction({
                    type: response.type,
                    payload: {
                      path: response.payload.path
                    }
                  });
                }} endIcon={<ArrowForward fontSize="small" />}>
                        {t("shell.navigate")}
                      </Button>;
              } else if (response.type === "MESSAGE") {
                return <Box data-cy="AIDrawerMessage" key={`${promptZUID}-${responseIndex}`}>
                        <AnimatedText text={response.payload.value} animate={shouldAnimate} onGrow={() => scrollToEnd(scrollContainerRef.current)} />
                      </Box>;
              } else if (response.type === "CODE_EDIT") {
                return <Box data-cy="AIDrawerCodeEdit" key={`${promptZUID}-${responseIndex}`}>
                        <Typography variant="body2">
                          {t("shell.aiCodeEditSummary", {
                      fileName: response.payload.fileName,
                      added: response.payload.added,
                      removed: response.payload.removed
                    })}
                        </Typography>
                      </Box>;
              } else if (response.type === "NOTICE") {
                return <Typography data-cy="AIDrawerNotice" key={`${promptZUID}-${responseIndex}`} variant="caption" color="text.secondary">
                        {response.payload.value}
                      </Typography>;
              }
              return <Box key={`${promptZUID}-${responseIndex}`} sx={{
                maxWidth: response.payload?.value?.startsWith("3-") ? "unset" : 160
              }}>
                      {response.payload.refKey && <Typography variant="body3" fontWeight={600} color="text.disabled" sx={{
                  mb: 1.25
                }}>
                          {response.payload.refKey}
                        </Typography>}
                      {response.payload?.value?.startsWith("3-") ? <GeneratedImage src={response.payload.value} /> : <AnimatedText key={`${promptZUID}-${responseIndex}`} text={response.payload.value} animate={shouldAnimate && !isInCodeApp} onGrow={() => scrollToEnd(scrollContainerRef.current)} />}
                      {!autoApply && response.type === "SET_VALUE" && <Button data-cy="AIDrawerSetValue" variant="contained" sx={{
                  mt: 1.25
                }} disabled={response.approval === "1"} onClick={() => {
                  if (!urlChatZUID) return;
                  // Stays applicable when the ref is not on the page.
                  if (!applySetValue(response.payload.refKey, response.payload.value)) {
                    return;
                  }
                  updatePromptApprovalStatus({
                    chatZUID: urlChatZUID,
                    promptZUID,
                    approval: "1"
                  });
                  // Optimistically mark only this response as approved
                  // (not every SET_VALUE response for the prompt) so
                  // applying one field doesn't silently disable Apply
                  // on the prompt's other generated fields.
                  // `prev` may no longer have this key if the chat
                  // session was switched or cleared before this applies.
                  setResponses(prev => {
                    const promptResponses = prev[promptZUID];
                    if (!promptResponses) return prev;
                    return {
                      ...prev,
                      [promptZUID]: promptResponses.map(promptResponse => promptResponse === response ? {
                        ...promptResponse,
                        approval: "1"
                      } : promptResponse)
                    };
                  });
                }} startIcon={<AutoFixHighRounded fontSize="small" />}>
                          {t("shell.applyAiSuggestion")}
                        </Button>}
                    </Box>;
            });
          })}
            {isLoading && <Box display="flex" alignItems="center" gap={0.5} p={1} borderRadius={2} border="1px solid" borderColor="border" width="fit-content" mb={1}>
                <Skeleton variant="rounded" width={8} height={8} />
                <Skeleton variant="rounded" width={8} height={8} />
                <Skeleton variant="rounded" width={8} height={8} />
              </Box>}
          </Box>
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" justifyContent="space-between" rowGap={1.5} px={2} mt={1} data-sentry-element="Box" data-sentry-source-file="ChatThread.tsx">
        {!Object.entries(responses)?.length && <Button disabled={isLoading || isLoadingChatSessionLog} data-cy="AIDrawerGenerateSuggestions" data-amp-track-id="ai-drawer-generate-suggestions-button" size="large" variant="outlined" fullWidth onClick={() => handleGenerateSuggestions(composerRef.current?.getDraft() ?? "")}>
            {t("shell.generateSuggestions")}
          </Button>}
        <PromptComposer ref={composerRef} seed={composerSeed} disabled={isLoading || isLoadingChatSessionLog} onSubmit={handlePrompt} onHasValueChange={setHasComposerValue} placeholder={placeholder} data-sentry-element="PromptComposer" data-sentry-source-file="ChatThread.tsx" />
      </Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" px={2} pb={2} pt={1.5} data-sentry-element="Box" data-sentry-source-file="ChatThread.tsx">
        <Button data-cy="AIDrawerClearChat" data-amp-track-id="ai-drawer-clear-chat-button" variant="text" color="inherit" onClick={onClearChat} data-sentry-element="Button" data-sentry-source-file="ChatThread.tsx">
          {t("shell.clearChat")}
        </Button>
        <Box display="flex" gap={1} data-sentry-element="Box" data-sentry-source-file="ChatThread.tsx">
          <IconButton data-cy="AIDrawerSettings" data-amp-track-id="ai-drawer-settings-button" onClick={() => setSettingsOpen(!settingsOpen)} size="small" data-sentry-element="IconButton" data-sentry-source-file="ChatThread.tsx">
            <SettingsRoundedIcon color="action" fontSize="small" data-sentry-element="SettingsRoundedIcon" data-sentry-source-file="ChatThread.tsx" />
          </IconButton>

          <Button data-cy="AIDrawerSend" data-amp-track-id="ai-drawer-submit-button" variant="contained" onClick={() => composerRef.current?.submit()} disabled={!hasComposerValue || isLoading || isLoadingChatSessionLog} sx={{
          flexShrink: 0,
          flexBasis: 32,
          borderRadius: 6,
          padding: 0.5,
          minWidth: 0,
          backgroundColor: !hasComposerValue || isLoading || isLoadingChatSessionLog ? "transparent!important" : "primary.main",
          color: !hasComposerValue || isLoading || isLoadingChatSessionLog ? "action.active" : "primary.contrastText"
        }} data-sentry-element="Button" data-sentry-source-file="ChatThread.tsx">
            <ArrowUpwardRounded fontSize="small" data-sentry-element="ArrowUpwardRounded" data-sentry-source-file="ChatThread.tsx" />
          </Button>
        </Box>
      </Box>
      <Collapse orientation="vertical" collapsedSize={0} sx={{
      position: "relative",
      flexShrink: 0,
      px: 2,
      pb: settingsOpen ? 2 : 0
    }} in={settingsOpen} data-sentry-element="Collapse" data-sentry-source-file="ChatThread.tsx">
        {!hideAutoApply && <FormGroup>
            <FormControlLabel sx={{
          mx: 0
        }} control={<Switch data-cy="AIDrawerAutoApplyToggle" inputProps={{
          // @ts-expect-error data attribute
          "data-amp-track-id": "ai-drawer-auto-apply-toggle"
        }} size="small" checked={autoApply} onChange={e => setAutoApply(e.target.checked)} />} label={<Typography variant="subtitle2" color="text.secondary">
                  {t("shell.autoApply")}
                </Typography>} />
          </FormGroup>}

        <Stack direction="row" gap={1} alignItems="center" mt={1} data-sentry-element="Stack" data-sentry-source-file="ChatThread.tsx">
          <InputLabel sx={{
          mb: 0
        }} data-sentry-element="InputLabel" data-sentry-source-file="ChatThread.tsx">{t("shell.language")}</InputLabel>
          <Tooltip title={t("shell.languageGenerationTooltip")} placement="top" data-sentry-element="Tooltip" data-sentry-source-file="ChatThread.tsx">
            <InfoRoundedIcon color="action" sx={{
            fontSize: 12
          }} data-sentry-element="InfoRoundedIcon" data-sentry-source-file="ChatThread.tsx" />
          </Tooltip>
        </Stack>
        <Autocomplete autoHighlight disableClearable isOptionEqualToValue={(option: any, value: any) => option.value === value.value} onChange={(event, value) => setSelectedLanguage(value)} value={selectedLanguage} options={languageOptions} renderInput={(params: any) => <TextField {...params} data-cy="AIDrawerLanguageSelect" inputProps={{
        ...params.inputProps,
        "data-amp-track-id": "ai-drawer-language-selector"
      }} fullWidth InputProps={{
        ...params.InputProps,
        startAdornment: <InputAdornment position="start" sx={{
          width: 24
        }}>
                    <LanguageRoundedIcon fontSize="small" />
                  </InputAdornment>
      }} />} data-sentry-element="Autocomplete" data-sentry-source-file="ChatThread.tsx" />

        <Stack direction="row" gap={1} alignItems="center" mt={1} data-sentry-element="Stack" data-sentry-source-file="ChatThread.tsx">
          <InputLabel sx={{
          mb: 0
        }} data-sentry-element="InputLabel" data-sentry-source-file="ChatThread.tsx">{t("shell.tone")}</InputLabel>
          <Tooltip title={t("shell.toneTooltip")} placement="top" data-sentry-element="Tooltip" data-sentry-source-file="ChatThread.tsx">
            <InfoRoundedIcon color="action" sx={{
            fontSize: 12
          }} data-sentry-element="InfoRoundedIcon" data-sentry-source-file="ChatThread.tsx" />
          </Tooltip>
        </Stack>
        <Autocomplete autoHighlight disableClearable isOptionEqualToValue={(option: any, value: any) => option.value === value.value} onChange={(_, value) => setSelectedTone(value)} value={selectedTone} options={toneOptions} renderInput={(params: any) => <TextField {...params} data-cy="AIDrawerToneSelect" inputProps={{
        ...params.inputProps,
        "data-amp-track-id": "ai-drawer-tone-selector"
      }} fullWidth />} data-sentry-element="Autocomplete" data-sentry-source-file="ChatThread.tsx" />
      </Collapse>
    </>;
};